import { FC, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  FormController,
  MuiGrid,
  MuiSelect,
  MuiTextField,
  OptionsDataType,
} from "../../../../components";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { ADD_USER_TABS, UserDetailKey } from "../../modelsAndSchemas/models";

import {
  useGetDateFormatMasterList,
  useGetServiceOfficeListByCountryCodesQuery,
} from "../../../../api/services/manageUser/manageUserApi";

import { MAX_LENGTH_75 } from "../../../../helpers/constants";
import {
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { useToast } from "../../../../hooks/useToast";
import { toggleLoader } from "../../../../redux/slices/appState/appStateSlice";
import { setCreateUserHomeOfficeDetail } from "../../../../redux/slices/manageUser/manageUserSlice";
import { createUserSiteCoreKeys } from "../../siteCore/SiteCoreKeys";

interface CreateUserUserDetailProps {
  pageId: string;
  countryListOptions: OptionsDataType[];
  timeZoneListOptions: OptionsDataType[];
  isEditUser: boolean;
  selectedTab: number;
}

export const CreateUserUserDetail: FC<CreateUserUserDetailProps> = ({
  pageId,
  countryListOptions,
  timeZoneListOptions,
  isEditUser,
  selectedTab,
}) => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { control, watch, getValues, setValue } = useFormContext();
  const [isCountryReset, setIsCountryReset] = useState(false);
  const { commonLabels, fetchLabel, fetchDropdownValues } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);

  const { fetchLanguageDropdownValues } = useNavigationSiteCore();

  const {
    data: serviceOfficeListByCountryCodes,
    refetchWithParams: refetchServiceOfficeListByCountryCodes,
    isFetching: isServiceOfficeFetching,
    isError: isServiceOfficeError,
  } = useGetServiceOfficeListByCountryCodesQuery();

  const { data: dateFormatListData } = useGetDateFormatMasterList();

  useEffect(() => {
    if (isServiceOfficeError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isServiceOfficeError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        createUser: {
          isVisible: isServiceOfficeFetching,
        },
      }),
    );
    return () => {
      dispatch(toggleLoader({ createUser: { isVisible: false } }));
    };
  }, [isServiceOfficeFetching]);

  useEffect(() => {
    isCountryReset && setValue(UserDetailKey.homeOffice, "");
    const { userCountry } = getValues();
    if (isNotEmpty(userCountry)) {
      refetchServiceOfficeListByCountryCodes({ countryCode: userCountry });
      setIsCountryReset(true);
    }
    // Reset home office when country changes
  }, [watch(UserDetailKey.userCountry)]);

  const getUserDetailLabel = (key: string) =>
    fetchLabel(createUserSiteCoreKeys.userDetailsKey, key);

  const serviceOfficeOptions = useMemo(() => {
    return setApiDataToDropDownOption(serviceOfficeListByCountryCodes);
  }, [serviceOfficeListByCountryCodes]);

  useEffect(() => {
    const { homeOffice } = getValues();
    if (
      selectedTab === ADD_USER_TABS.USER_DETAIL &&
      isNotEmpty(serviceOfficeOptions)
    ) {
      dispatch(
        setCreateUserHomeOfficeDetail(
          serviceOfficeOptions?.find(
            (data: OptionsDataType) => data.value === homeOffice,
          )?.label ?? "",
        ),
      );
    }
  }, [watch(UserDetailKey.homeOffice), serviceOfficeOptions]);

  return (
    <MuiGrid container className="user-detail-grid">
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={UserDetailKey.preferredName}>
          <MuiTextField
            id={`${pageId}-${UserDetailKey.preferredName}`}
            label={getUserDetailLabel(createUserSiteCoreKeys.preferredName)}
            placeholder={
              isEditUser
                ? ""
                : getUserDetailLabel(
                    createUserSiteCoreKeys.preferredNamePlaceHolder,
                  )
            }
            maxLength={MAX_LENGTH_75}
            shouldTrimSpaces
            requiredARIALabel={commonARIALabel.required}
            disabled={isEditUser}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={UserDetailKey.userEmail}>
          <MuiTextField
            label={getUserDetailLabel(
              createUserSiteCoreKeys.userEmailPlaceHolder,
            )}
            id={`${pageId}-${UserDetailKey.userEmail}`}
            disabled
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={UserDetailKey.affliateEmailId}>
          <MuiTextField
            id={`${pageId}-${UserDetailKey.affliateEmailId}`}
            label={getUserDetailLabel(createUserSiteCoreKeys.affiliateEmailId)}
            placeholder={getUserDetailLabel(
              createUserSiteCoreKeys.affiliateEmailIdPlaceHolder,
            )}
            maxLength={MAX_LENGTH_75}
            shouldTrimSpaces
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={UserDetailKey.userCountry}>
          <MuiSelect
            label={getUserDetailLabel(createUserSiteCoreKeys.userCountry)}
            options={countryListOptions}
            visibleDefaultSelectOption
            value=""
            id={`${pageId}-${UserDetailKey.userCountry}`}
            placeholder={commonLabels.selectPlaceHolder}
            isMandatory
            requiredARIALabel={commonARIALabel.required}
            showSearch
            searchPlaceholder={commonLabels.search}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={UserDetailKey.homeOffice}>
          <MuiSelect
            label={getUserDetailLabel(createUserSiteCoreKeys.homeOffice)}
            options={serviceOfficeOptions}
            visibleDefaultSelectOption
            value=""
            id={`${pageId}-${UserDetailKey.homeOffice}`}
            placeholder={commonLabels.selectPlaceHolder}
            isMandatory
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController
          control={control}
          name={UserDetailKey.applicationLanguage}
        >
          <MuiSelect
            label={getUserDetailLabel(
              createUserSiteCoreKeys.applicationLanguage,
            )}
            options={fetchLanguageDropdownValues()}
            visibleDefaultSelectOption
            value=""
            id={`${pageId}-${UserDetailKey.applicationLanguage}`}
            placeholder={commonLabels.selectPlaceHolder}
            isMandatory
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={UserDetailKey.timeZone}>
          <MuiSelect
            label={getUserDetailLabel(createUserSiteCoreKeys.timeZone)}
            options={timeZoneListOptions}
            visibleDefaultSelectOption
            value=""
            id={`${pageId}-${UserDetailKey.timeZone}`}
            placeholder={commonLabels.selectPlaceHolder}
            isMandatory
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={UserDetailKey.userStatus}>
          <MuiSelect
            label={getUserDetailLabel(createUserSiteCoreKeys.userStatus)}
            options={fetchDropdownValues(
              createUserSiteCoreKeys.userDetailsKey,
              createUserSiteCoreKeys.statusList,
            )}
            value=""
            id={`${pageId}-${UserDetailKey.userStatus}`}
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={UserDetailKey.dateFormat}>
          <MuiSelect
            label={getUserDetailLabel(createUserSiteCoreKeys.dateFormat)}
            options={setApiDataToDropDownOption(dateFormatListData)}
            visibleDefaultSelectOption
            value=""
            isMandatory
            id={`${pageId}-${UserDetailKey.dateFormat}`}
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
    </MuiGrid>
  );
};
